import * as React from 'react'
import { getAffiliateIdFromStorage } from '@repo/affiliate-id'
import {
  usePaymentsAutoStartSubscriptionMutation,
  paymentsAutoStartSubscriptionMutationKey,
  usePaymentsChangeSubscriptionPlanMutation,
  paymentsChangeSubscriptionPlanMutationKey,
} from '@repo/studio-hooks-coins/gen/coins'
import type { SubscriptionPlan } from 'codegen/types'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import {
  useCoinSubscriptionQuery,
  useUserSubscriptionIdQuery,
} from '@repo/cf-user/hooks/use-user-data'
import { useIsMutating } from '@tanstack/react-query'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { toast } from '@repo/studio-components-toast'
import { tryOpenPaymentRequiredDialogFromSubscription } from '@repo/studio-hooks-payments/use-payment-required-dialog'
import {
  DEFAULT_COIN_PLAN,
  SUBSCRIPTION_PLAN_TO_COIN_PLAN,
  getTierForSubscriptionPlan,
  toAvailableCurrencyCode,
} from '@repo/studio-hooks-coins/constants/coin-top-up'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { usePaymentsDialog } from '@repo/studio-hooks-payments/use-payments-dialog'
import { isCombinedCurrencies } from 'payments/utils'

export function useStartCoinSubscription(
  subscriptionPlan: SubscriptionPlan,
  options?: { onSuccess?: () => void },
) {
  const t = useStudioTranslate()
  const { currency } = usePricesForUser()
  const { data: coinSub } = useCoinSubscriptionQuery()
  const { data: subscriptionId } = useUserSubscriptionIdQuery('coins')
  const autoStartMutation = usePaymentsAutoStartSubscriptionMutation()
  const changePlanMutation = usePaymentsChangeSubscriptionPlanMutation()
  const isAutoStartPending =
    useIsMutating({ mutationKey: [paymentsAutoStartSubscriptionMutationKey] }) > 0
  const isChangePending =
    useIsMutating({ mutationKey: [paymentsChangeSubscriptionPlanMutationKey] }) > 0
  const isPending = isAutoStartPending || isChangePending
  const onSuccess = options?.onSuccess
  const isSubscribed = coinSub?.isSubscribed ?? false
  const { open: openPaymentsDialog } = usePaymentsDialog()

  const showErrorToast = React.useCallback(() => {
    toast({
      title: t('studio.components.coin_top_up_message.toasts.error_generic.title'),
      description: t('studio.components.coin_top_up_message.toasts.error_generic.description'),
    })
  }, [t])

  const showDifferentCurrencyToast = React.useCallback(() => {
    toast({
      title: t('studio.components.coin_top_up_message.toasts.error_different_currency.title'),
      description: t(
        'studio.components.coin_top_up_message.toasts.error_different_currency.description',
      ),
    })
  }, [t])

  const handleSubmit = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (isSubscribed && subscriptionId) {
        changePlanMutation.mutate(
          {
            input: {
              subscriptionId,
              newPlan: subscriptionPlan,
            },
          },
          {
            onSuccess(data) {
              const { errors, subscription } = data.paymentsChangeSubscriptionPlan
              if (errors?.length) {
                showErrorToast()
                return
              }
              if (tryOpenPaymentRequiredDialogFromSubscription(subscription)) {
                return
              }
              const tier = getTierForSubscriptionPlan(subscriptionPlan)
              if (tier) {
                sendAmplitudeEvent(AmplitudeEvents.PURCHASED_COIN_PACK, {
                  coin_pack_type: 'subscription',
                  coin_pack: `${tier.coins} coins`,
                  price: tier.priceUsd,
                })
              }
              onSuccess?.()
            },
            onError: showErrorToast,
          },
        )
        return
      }

      autoStartMutation.mutate(
        {
          input: {
            subscriptionPlan,
            currencyCode: toAvailableCurrencyCode(currency.code),
            isTrial: false,
            // Forward the captured ref for apps that can't share the affwp_ref cookie
            // cross-domain. On cookie-bearing domains BE reads the cookie directly.
            affiliateId: getAffiliateIdFromStorage() ?? undefined,
          },
        },
        {
          onSuccess(data) {
            const { errors, subscription } = data.paymentsAutoStartSubscription
            if (errors?.length || !subscription) {
              if (isCombinedCurrencies(errors)) {
                showDifferentCurrencyToast()
                return
              }

              const coinPlan = SUBSCRIPTION_PLAN_TO_COIN_PLAN[subscriptionPlan]
              openPaymentsDialog(coinPlan ?? DEFAULT_COIN_PLAN)
              return
            }

            if (tryOpenPaymentRequiredDialogFromSubscription(subscription)) {
              return
            }

            const tier = getTierForSubscriptionPlan(subscriptionPlan)
            if (tier) {
              sendAmplitudeEvent(AmplitudeEvents.PURCHASED_COIN_PACK, {
                coin_pack_type: 'subscription',
                coin_pack: `${tier.coins} coins`,
                price: tier.priceUsd,
              })
            }
            onSuccess?.()
          },
          onError: showErrorToast,
        },
      )
    },
    [
      autoStartMutation,
      changePlanMutation,
      currency.code,
      isSubscribed,
      onSuccess,
      openPaymentsDialog,
      showErrorToast,
      showDifferentCurrencyToast,
      subscriptionId,
      subscriptionPlan,
    ],
  )

  return { handleSubmit, isPending }
}
